/**
 * Product Detail Page Wrapper for ISR
 *
 * Performance optimizations:
 * - Desktop and Mobile variants are in SEPARATE files with their own imports
 * - Each variant uses dynamic() for below-the-fold components
 * - Only the needed platform variant is loaded via dynamic import
 * - Above-the-fold components are statically imported in each variant
 *
 * The old monolithic file with 20+ static imports for BOTH platforms is replaced
 * by this lightweight wrapper that selects the right platform variant.
 */

'use client';

import React, {useEffect, useState} from 'react';
import dynamic from 'next/dynamic';
import NotFound from '@/_application/core/components/ui/not-found';
import {DynamicPageServerSideProps} from '@/_application/core/types';
import NotificationPopup from '@/_application/modules/products/details/notification-popup';
import {AttributesProvider} from '@/store/attributes/attributes.context';
import {CategoryProductsSeo} from '@/_application/modules/categories/components/seo/category-products.seo';
import {useIsrPageInit} from '@/_application/pages/_isr';

// Platform-specific components — only the needed variant is loaded
const ProductDetailDesktopIsr = dynamic(() => import('./product-detail-desktop-isr'), {ssr: true});
const ProductDetailMobileIsr = dynamic(() => import('./product-detail-mobile-isr'), {ssr: true});

export interface ProductDetailPageWrapperProps {
    variables?: DynamicPageServerSideProps['variables'];
    isMobile?: boolean;
    layout?: DynamicPageServerSideProps['layout'];
}

/**
 * Product Detail Page Wrapper for ISR
 * Entry point that selects mobile or desktop version
 * Uses centralized useIsrPageInit to handle storeServerData
 */
export const ProductDetailPageWrapper: React.FC<ProductDetailPageWrapperProps> = ({variables, isMobile}) => {
    // Initialize ISR page - handles storeServerData and returns customer
    const customer = useIsrPageInit(variables);
    const {productDetail} = variables ?? {};
    const {product, mediaGallery, reviews} = productDetail ?? {};
    const [hasScrolled, setHasScrolled] = useState(false);
    const isServer = globalThis.window === undefined;

    // Scroll handler for lazy loading NotificationPopup
    useEffect(() => {
        const handleScroll = () => {
            setHasScrolled(true);
            window.removeEventListener('scroll', handleScroll);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Safety check - if product is missing, show NotFound
    // This should not happen in normal flow as getStaticProps validates urlResolver
    if (!product) return <NotFound />;

    return (
        <AttributesProvider>
            {!isServer && <CategoryProductsSeo product={product} reviews={reviews ?? []} />}

            {isMobile ? (
                <ProductDetailMobileIsr
                    product={product}
                    mediaGallery={mediaGallery ?? []}
                    reviews={reviews ?? []}
                    customer={customer}
                />
            ) : (
                <ProductDetailDesktopIsr
                    product={product}
                    mediaGallery={mediaGallery ?? []}
                    reviews={reviews ?? []}
                    customer={customer}
                />
            )}

            {hasScrolled && <NotificationPopup product={product} />}
        </AttributesProvider>
    );
};

export default ProductDetailPageWrapper;
